import React, { useCallback, useMemo, useState } from 'react';
import { Tooltip } from 'antd';
import { formatPhoneNumberCopy, formatPhoneNumberDisplay } from '../utils';

type PhoneNumberDisplayProps = {
  value?: string | null;
  className?: string;
  emptyText?: React.ReactNode;
};

const copyToClipboard = async (text: string): Promise<boolean> => {
  if (!text) {
    return false;
  }

  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    // Ignore and fallback to execCommand
  }

  if (typeof document === 'undefined') {
    return false;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);

  const selection = document.getSelection();
  const selected = selection ? selection.rangeCount > 0 ? selection.getRangeAt(0) : null : null;

  textarea.select();
  const success = document.execCommand('copy');
  document.body.removeChild(textarea);

  if (selected && selection) {
    selection.removeAllRanges();
    selection.addRange(selected);
  }

  return success;
};

const PhoneNumberDisplay: React.FC<PhoneNumberDisplayProps> = ({ value, className, emptyText = '-' }) => {
  const [tooltipText, setTooltipText] = useState('Kopyala');

  const displayValue = useMemo(() => formatPhoneNumberDisplay(value), [value]);
  const copyValue = useMemo(() => formatPhoneNumberCopy(value), [value]);

  const triggerCopy = useCallback(async () => {
    if (!copyValue) {
      return;
    }

    const copied = await copyToClipboard(copyValue);
    setTooltipText(copied ? 'Kopyalandı' : 'Kopyalanamadı');
    setTimeout(() => setTooltipText('Kopyala'), 1500);
  }, [copyValue]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    triggerCopy();
  }, [triggerCopy]);

  const handleKeyPress = useCallback((event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      triggerCopy();
    }
  }, [triggerCopy]);

  if (!displayValue) {
    return <span className={className}>{emptyText}</span>;
  }

  return (
    <Tooltip title={tooltipText} placement="top">
      <span
        className={className}
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyPress={handleKeyPress}
      >
        {displayValue}
      </span>
    </Tooltip>
  );
};

export default PhoneNumberDisplay;
